import { cn } from "@/lib/utils";
import { Skeleton } from "@/components/ui/skeleton";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { ColumnOption } from "@/types";
import Metrics from "@/components/common/metrics";
import { useMemo } from "react";

// Metric Card Component
interface MetricCardProps {
  title?: string;
  value?: number | string;
  change?: number | string;
  hasDropdown?: boolean;
  selectedMetric?: string;
  onMetricChange?: (metric: string) => void;
  className?: string;
  prefix?: string;
  suffix?: string;
  loading?: boolean;
  metricOptions?: ColumnOption[];
  defaultMetric?: string;
  onSetDefault?: (metric: string) => void;
  sparkGraph?: React.ReactNode;
}

const MetricCard: React.FC<MetricCardProps> = ({
  title,
  value,
  change,
  hasDropdown = false,
  selectedMetric,
  onMetricChange,
  className,
  prefix,
  suffix,
  loading,
  metricOptions,
  defaultMetric,
  onSetDefault,
  sparkGraph,
}) => {
  const getChangeType = () => {
    if (change === undefined) return null;
    if (Number(change) === 0 || change === "N/A") return "neutral";
    return Number(change) > 0 ? "positive" : "negative";
  };
  const getChangeColor = () => {
    switch (getChangeType()) {
      case "positive":
        return "text-[#009B4D]";
      case "negative":
        return "text-[#E81616]";
      default:
        return "text-brand-primary-400";
    }
  };
  const maxChange = 9999;
  const formatChange = (change: number | string | undefined) => {
    if (change === "N/A") return "-";
    if (change === undefined || isNaN(Number(change))) return null;
    if (change === 0) return "0%";
    const changeSign = Number(change) > 0 ? "+" : "-";
    const absChange = Math.abs(Number(change));
    const adjustedChange = absChange > maxChange ? maxChange : absChange;
    return `${changeSign}${adjustedChange}%`;
  };
  const getTooltipContent = (change: number | string | undefined) => {
    if (change === "N/A") return "N/A";
    if (change === undefined || isNaN(Number(change))) return null;
    if (change === 0) return "No Change";
    if (Math.abs(Number(change)) > maxChange)
      return `${Number(change) > 0 ? "+" : ""}${change}%`;
    return null;
  };
  const changeColor = getChangeColor();
  const formattedValue = value === "N/A" ? "—" : value?.toLocaleString();
  const formattedChange = formatChange(change);
  const tooltipContent = getTooltipContent(change);

  // Prepare selectedMetrics for Metrics component
  const selectedMetrics = useMemo(() => {
    if (!selectedMetric || !metricOptions) return [];
    const metric = metricOptions.find((m) => m.key === selectedMetric);
    return metric ? [metric] : [];
  }, [selectedMetric, metricOptions]);

  // Handler to convert single metric to string for onMetricChange
  const handleMetricChange = (metrics: ColumnOption[]) => {
    if (metrics.length > 0 && onMetricChange) {
      onMetricChange(metrics[0].key);
    }
  };

  return (
    <div
      className={cn(
        "flex items-center gap-5 bg-white rounded-l-[14px] px-[12px] py-[6px] border-r last:rounded-r-[14px] last:border-none",
        className,
      )}
    >
      <div>
        <div className="h-[23px] flex items-end justify-start w-full overflow-hidden">
          {title && <span className="text-[11px] text-brand-primary-800">{title}</span>}
          {hasDropdown && selectedMetric !== undefined && onMetricChange && (
            <Metrics
              renderingStyle="single_select"
              selectedMetrics={selectedMetrics}
              onMetricChange={handleMetricChange}
              className="text-[12px]"
              metricsOptions={metricOptions || []}
              defaultMetric={defaultMetric}
              onSetDefault={onSetDefault}
            />
          )}
        </div>
        {loading ? (
          <div className="flex items-center gap-2 h-[31px]">
            <Skeleton className="w-[60px] h-[20px]" />
            <Skeleton className="w-[40px] h-[20px]" />
          </div>
        ) : (
          <div className="flex items-center leading-[31px]">
            {prefix && formattedValue !== "—" && (
              <span className="text-[16px] font-normal text-brand-primary-800">
                {prefix}
              </span>
            )}
            {formattedValue === "—" ? (
              <Tooltip>
                <TooltipTrigger>
                  <span className="text-[16px] font-normal text-brand-primary-800">
                    {formattedValue}
                  </span>
                </TooltipTrigger>
                <TooltipContent side="bottom" align="start" alignOffset={18}>
                  <p>N/A</p>
                </TooltipContent>
              </Tooltip>
            ) : (
              <span className="text-[16px] font-normal text-brand-primary-800">
                {formattedValue}
              </span>
            )}
            {suffix && formattedValue !== "—" && (
              <span className="text-[16px] font-normal text-brand-primary-800">
                {suffix}
              </span>
            )}
            {change === "__LOADING__" && <Skeleton className="w-[40px] h-[20px]" />}
            {change !== "__LOADING__" &&
              formattedChange &&
              formattedValue !== "—" &&
              (tooltipContent ? (
                <Tooltip>
                  <TooltipTrigger>
                    <span className={`ml-[6px] text-[12px] font-normal ${changeColor}`}>
                      {formattedChange}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" align="start" alignOffset={18}>
                    <p>{tooltipContent}</p>
                  </TooltipContent>
                </Tooltip>
              ) : (
                <span className={`ml-[6px] text-[12px] font-normal ${changeColor}`}>
                  {formattedChange}
                </span>
              ))}
          </div>
        )}
      </div>
      {sparkGraph && <div>{sparkGraph}</div>}
    </div>
  );
};

export default MetricCard;
